import { useRef, useState } from "react";
import { Coin, ChoiceFace, Shape } from "@/components/learn/Visuals";

const TILE = "min-h-[56px] min-w-[56px] rounded-2xl px-4 font-display text-xl font-extrabold transition-[transform,background-color,opacity] duration-150 focus-visible:outline focus-visible:outline-4 focus-visible:outline-[rgb(var(--glow-kids))] motion-safe:active:scale-95";

// Arrow keys move focus between the buttons inside a board.
function arrows(e) {
  if (!["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp"].includes(e.key)) return;
  const els = [...e.currentTarget.querySelectorAll("button:not([disabled])")];
  const i = els.indexOf(document.activeElement);
  if (i < 0) return;
  e.preventDefault();
  els[(i + (["ArrowRight", "ArrowDown"].includes(e.key) ? 1 : -1) + els.length) % els.length].focus();
}

export function ChoiceBoard({ q, wrong, disabled, onPick }) {
  return (
    <div className={`grid gap-3 ${q.choices.length === 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"}`} onKeyDown={arrows} role="group" aria-label="Choices">
      {q.choices.map((c) => (
        <button key={c.id} disabled={disabled || wrong.includes(c.id)} onClick={() => onPick(c.id)}
          className={`${TILE} min-h-[72px] ${wrong.includes(c.id) ? "bg-white/5 text-q-faint line-through opacity-50" : "bg-[rgba(var(--glow-kids),0.16)] text-q-text hover:bg-[rgba(var(--glow-kids),0.3)]"}`}
          data-testid={`choice-${c.id}`}><ChoiceFace c={c} /></button>
      ))}
    </div>
  );
}

export function TextBoard({ disabled, onSubmit }) {
  const [v, setV] = useState("");
  return (
    <form className="flex flex-col gap-3 sm:flex-row" onSubmit={(e) => { e.preventDefault(); if (v.trim()) { onSubmit(v); setV(""); } }}>
      <input autoFocus autoCapitalize="none" autoCorrect="off" spellCheck={false} className="q-input min-h-[56px] flex-1 text-center font-display text-2xl tracking-widest" value={v} onChange={(e) => setV(e.target.value)} disabled={disabled} aria-label="Type the word" data-testid="spell-input" />
      <button className={`${TILE} bg-[rgb(var(--glow-kids))] text-white`} disabled={disabled} data-testid="spell-submit">Check</button>
    </form>
  );
}

// Tap tiles (or dots) in order. Works by tap, Tab+Enter, and drag onto the answer row.
export function OrderBoard({ q, disabled, onSubmit }) {
  const [picked, setPicked] = useState([]);
  const trace = q.visual?.kind === "trace";
  const need = q.length || q.items.length;
  const add = (id) => {
    if (disabled || picked.includes(id)) return;
    const next = [...picked, id];
    setPicked(next);
    if (next.length === need) { onSubmit(next); setTimeout(() => setPicked([]), 700); }
  };
  const lab = Object.fromEntries(q.items.map((i) => [i.id, i.label]));
  if (trace) {
    const pts = q.items.filter((d) => picked.includes(d.id)).sort((a, b) => picked.indexOf(a.id) - picked.indexOf(b.id));
    return (
      <div className="relative mx-auto aspect-square w-full max-w-[320px] rounded-3xl bg-white/5" onKeyDown={arrows} data-testid="trace-board">
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center font-display text-[220px] font-extrabold leading-none text-white/10">{q.visual.letter}</span>
        <svg viewBox="0 0 100 100" className="pointer-events-none absolute inset-0 h-full w-full"><polyline points={pts.map((d) => `${d.x},${d.y}`).join(" ")} fill="none" stroke="rgb(var(--glow-kids))" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        {q.items.map((d) => (
          <button key={d.id} onClick={() => add(d.id)} disabled={disabled} style={{ left: `${d.x}%`, top: `${d.y}%` }}
            className={`absolute h-14 w-14 -translate-x-1/2 -translate-y-1/2 rounded-full font-display text-xl font-extrabold focus-visible:outline focus-visible:outline-4 ${picked.includes(d.id) ? "bg-[rgb(var(--glow-kids))] text-white" : "bg-white text-slate-900"}`} data-testid={`dot-${d.label}`}>{d.label}</button>
        ))}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="flex min-h-[64px] flex-wrap justify-center gap-2 rounded-3xl border-2 border-dashed border-white/20 p-2" onDragOver={(e) => e.preventDefault()} onDrop={(e) => add(e.dataTransfer.getData("text/plain"))} data-testid="order-answer">
        {Array.from({ length: need }, (_, i) => <span key={i} className={`flex h-14 w-14 items-center justify-center rounded-2xl font-display text-2xl font-extrabold ${picked[i] ? "bg-[rgb(var(--glow-kids))] text-white" : "bg-white/5 text-q-faint"}`}>{picked[i] ? lab[picked[i]] : ""}</span>)}
      </div>
      <div className="flex flex-wrap justify-center gap-2" onKeyDown={arrows}>
        {q.items.map((t) => <button key={t.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", t.id)} disabled={disabled || picked.includes(t.id)} onClick={() => add(t.id)} className={`${TILE} bg-white text-slate-900 disabled:opacity-30`} data-testid={`tile-${t.id}`}>{t.label}</button>)}
      </div>
      <button className={`${TILE} mx-auto block border border-q-line text-base text-q-muted`} onClick={() => setPicked([])} disabled={disabled || !picked.length} data-testid="order-clear">Start again</button>
    </div>
  );
}

const ItemFace = ({ v }) => (v.kind === "coin" ? <Coin coin={v.coin} labelled={v.labelled} /> : <Shape shape={v.shape} color={v.color} size={48} />);

// Sort items into bins: tap an item then a bin, Enter on each, or drag the item onto a bin.
export function SortBoard({ q, disabled, onSubmit }) {
  const [placed, setPlaced] = useState({});
  const [sel, setSel] = useState(null);
  const done = useRef(false);
  const put = (item, bin) => {
    if (disabled || !item) return;
    const next = { ...placed, [item]: bin };
    setPlaced(next);
    setSel(null);
    if (Object.keys(next).length === q.items.length && !done.current) { done.current = true; onSubmit(next); setTimeout(() => { setPlaced({}); done.current = false; }, 900); }
  };
  const loose = q.items.filter((i) => !placed[i.id]);
  return (
    <div className="space-y-4" onKeyDown={arrows}>
      <div className="flex min-h-[72px] flex-wrap justify-center gap-2" data-testid="sort-items">
        {loose.map((i, n) => (
          <button key={i.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", i.id)} onClick={() => setSel(sel === i.id ? null : i.id)} disabled={disabled}
            aria-label={`Item ${n + 1}${sel === i.id ? ", picked" : ""}`} aria-pressed={sel === i.id}
            className={`${TILE} flex items-center justify-center bg-white/10 px-2 ${sel === i.id ? "ring-4 ring-[rgb(var(--glow-kids))] motion-safe:-translate-y-1" : ""}`} data-testid={`sort-item-${i.id}`}><ItemFace v={i.visual} /></button>
        ))}
      </div>
      <p className="text-center text-sm text-q-muted">{sel ? "Now tap the box it goes in" : loose.length ? "Tap something, then tap its box" : ""}</p>
      <div className={`grid gap-3 ${q.bins.length > 2 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2"}`}>
        {q.bins.map((b) => (
          <button key={b.id} onClick={() => put(sel, b.id)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => put(e.dataTransfer.getData("text/plain"), b.id)} disabled={disabled}
            className={`${TILE} flex min-h-[120px] flex-col items-center justify-start gap-1 border-2 py-2 text-base ${sel ? "border-[rgb(var(--glow-kids))] bg-[rgba(var(--glow-kids),0.12)]" : "border-white/15 bg-white/5"}`} data-testid={`sort-bin-${b.id}`}>
            <span className="text-q-text">{b.label}</span>
            <span className="flex flex-wrap justify-center gap-1">{q.items.filter((i) => placed[i.id] === b.id).map((i) => <span key={i.id} className="learn-pop scale-75"><ItemFace v={i.visual} /></span>)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
